/**
 * The field list behind an endpoint page's spec table, read out of the JSON
 * examples the catalog already publishes (`inputExample`, `sampleResponse`).
 *
 * The examples are the only field-level facts an endpoint page holds. The
 * gateway's JSON Schemas live behind `/public/try/{sku}/schema`, one request per
 * SKU, which a prerender crawl cannot afford - so a page that has no published
 * example lists no fields rather than guessing at them.
 *
 * The walk has no depth or row limit. Every bound here would be invented: the
 * examples are small, hand-authored documents and their own shape is what ends
 * the recursion.
 *
 * `responseFields` lists the JSON Schema the build projects out of
 * `/openapi.json`, valued from the published example where they share a path.
 */

import { parseSchema } from './schemaForm';
import { outlineSchema, type OutlineNode } from './schemaOutline';

export interface FieldRow {
  /** dotted path from the example root, e.g. `data.posts[].title` */
  name: string;
  /** JSON type of the example value */
  type: string;
  /** the example value as JSON; empty for an object or array, whose members
   *  are listed as rows of their own */
  value: string;
  /** the schema's documented meaning, present only on schema-derived rows */
  description?: string;
}

const jsonType = (value: unknown): string => {
  if (value === null) return 'null';
  if (Array.isArray(value)) return 'array';
  return typeof value;
};

const isContainer = (value: unknown): boolean =>
  value !== null && typeof value === 'object';

const row = (name: string, value: unknown): FieldRow => ({
  name,
  type: jsonType(value),
  value: isContainer(value) ? '' : JSON.stringify(value),
});

/** Appends `value`'s members, and their members, under `prefix`. */
const walk = (value: unknown, prefix: string, rows: FieldRow[]): void => {
  if (Array.isArray(value)) {
    // One row per array, then the shape of its first member under `[]`. A
    // published example lists one representative item, so the rest would
    // repeat it.
    const [first] = value;
    if (value.length === 0) return;
    const name = `${prefix}[]`;
    if (isContainer(first)) walk(first, name, rows);
    else rows.push(row(name, first));
    return;
  }
  if (value === null || typeof value !== 'object') return;
  for (const [key, member] of Object.entries(value as Record<string, unknown>)) {
    const name = prefix ? `${prefix}.${key}` : key;
    rows.push(row(name, member));
    if (isContainer(member)) walk(member, name, rows);
  }
};

/** The published example parsed as a JSON object, or null when there is none. */
export const exampleObject = (json?: string): Record<string, unknown> | null => {
  if (!json) return null;
  try {
    const parsed: unknown = JSON.parse(json);
    return parsed !== null && typeof parsed === 'object' && !Array.isArray(parsed)
      ? (parsed as Record<string, unknown>)
      : null;
  } catch {
    return null;
  }
};

/** Every field in a published JSON example, flattened to dotted paths. */
export const exampleFields = (json?: string): FieldRow[] => {
  if (!json) return [];
  let parsed: unknown;
  try {
    parsed = JSON.parse(json);
  } catch {
    return [];
  }
  const rows: FieldRow[] = [];
  walk(parsed, '', rows);
  return rows;
};

/** `outlineSchema`'s tree, flattened to the same dotted rows an example produces. */
const flatten = (nodes: OutlineNode[], prefix: string, rows: FieldRow[]): void => {
  for (const node of nodes) {
    const name = prefix ? `${prefix}.${node.name}` : node.name;
    rows.push({ name, type: node.type, value: '', description: node.description });
    // An array of objects lists its member shape under `[]`, matching the
    // example walk above, so the two sources produce one vocabulary of names.
    if (node.children) flatten(node.children, node.type.endsWith('[]') ? `${name}[]` : name, rows);
  }
};

/**
 * Field rows from a JSON Schema.
 *
 * The schema knows every field's name, type and documented meaning but no
 * values, so these rows carry a description where an example row carries a
 * value. That is the honest trade: the alternative is a synthesized `"string"`
 * that reads like real data.
 */
const schemaFields = (schema: unknown): FieldRow[] => {
  const rows: FieldRow[] = [];
  flatten(outlineSchema(schema), '', rows);
  return rows;
};

/**
 * Request rows: every field the input schema declares, carrying the example's
 * value where the example sets one.
 *
 * `parseSchema` rather than `outlineSchema`, because an input schema is flat and
 * often puts an `anyOf` at its root to say "identify the person by email, or by
 * LinkedIn URL, or by name plus domain". `outlineSchema` reads that union as the
 * shape and resolves to a branch carrying only `required`, which is how eleven
 * endpoints rendered no request fields at all despite declaring several.
 *
 * Listing the schema rather than the example also keeps the optional fields:
 * `apollo.person_enrich` accepts an `email` its example does not use, and a
 * reader deciding whether the endpoint fits needs to see it.
 */
export const requestFields = (
  schema: unknown,
  example: Record<string, unknown> | null,
): FieldRow[] =>
  parseSchema(schema).map((field) => ({
    name: field.name,
    type: field.kind,
    value: example && field.name in example ? JSON.stringify(example[field.name]) : '',
    description: field.description,
  }));

/**
 * Response rows: every field the output schema declares, carrying the authored
 * sample's value where the sample has the same dotted path. Falls back to the
 * sample's own rows only when the schema yields none.
 *
 * A sample is a short illustration, so listing it alone hid most of a response:
 * `linkedin.profile`'s sample shows 6 fields of the 72 its schema declares.
 */
export const responseFields = (schema: unknown, sample: FieldRow[]): FieldRow[] => {
  const declared = schemaFields(schema);
  if (declared.length === 0) return sample;
  const values = new Map(sample.map((field) => [field.name, field.value]));
  return declared.map((field) => ({ ...field, value: values.get(field.name) ?? '' }));
};

/**
 * Adds every dotted path in `value` under `prefix`, named as `walk` names them,
 * but across every member of an array rather than the first: a sample's later
 * item may carry a field its first item lacks, and that field is still shown.
 */
const collectPaths = (value: unknown, prefix: string, paths: Set<string>): void => {
  if (Array.isArray(value)) {
    for (const item of value) {
      if (isContainer(item)) collectPaths(item, `${prefix}[]`, paths);
      else paths.add(`${prefix}[]`);
    }
    return;
  }
  if (value === null || typeof value !== 'object') return;
  for (const [key, member] of Object.entries(value as Record<string, unknown>)) {
    const name = prefix ? `${prefix}.${key}` : key;
    paths.add(name);
    if (isContainer(member)) collectPaths(member, name, paths);
  }
};

/** Every dotted path an authored sample shows, across all its array items. */
const samplePaths = (json?: string): Set<string> => {
  const paths = new Set<string>();
  const parsed = exampleObject(json);
  if (parsed) collectPaths(parsed, '', paths);
  return paths;
};

export interface SampleCoverage {
  /** schema rows the sample has */
  shown: number;
  /** rows the schema declares */
  total: number;
}

/**
 * How much of the declared response an authored sample covers, counted in the
 * dotted paths `responseFields` lists. Null when there is no schema to count
 * against, or the sample already has every declared path.
 */
export const sampleCoverage = (schema: unknown, sampleJson?: string): SampleCoverage | null => {
  const declared = schemaFields(schema);
  const present = samplePaths(sampleJson);
  const shown = declared.filter((field) => present.has(field.name)).length;
  return shown < declared.length ? { shown, total: declared.length } : null;
};
